import { useMemo } from "react";
import { AccountType } from "shared/types/accounts";
import { PlatformType } from "./types";

/**
 * Filter accounts by platform(s)
 */
export function useAccountFiltering(
  accounts: AccountType[],
  platforms?: PlatformType[]
): AccountType[] {
  return useMemo(() => {
    if (!platforms || platforms.length === 0) {
      return accounts || [];
    }

    return (accounts || []).filter((account) => {
      // Check both platformName and platformType for Instagram
      if (platforms.includes("IG")) {
        return (
          account.platformName === "instagram" || account.platformType === "IG"
        );
      }
      // For other platforms, check platformType
      return platforms.includes(account.platformType as PlatformType);
    });
  }, [accounts, platforms]);
}

/**
 * Filter accounts by search query
 */
export function useAccountSearch(
  accounts: AccountType[],
  searchQuery: string
): AccountType[] {
  return useMemo(() => {
    if (!searchQuery) return accounts;

    const lowerQuery = searchQuery.toLowerCase();
    return accounts.filter((account) => {
      const accountName =
        account.displayLogin ||
        account.loginAlias ||
        account.brandName ||
        account.login ||
        "";
      return accountName.toLowerCase().includes(lowerQuery);
    });
  }, [accounts, searchQuery]);
}

/**
 * Group accounts by platform or other criteria
 */
export function useAccountGrouping(
  accounts: AccountType[]
): Array<{ title: string; accounts: AccountType[] }> {
  return useMemo(() => {
    const grouped: Record<string, AccountType[]> = {};

    accounts.forEach((account) => {
      const platform = account.platformType || "Other";
      if (!grouped[platform]) {
        grouped[platform] = [];
      }
      grouped[platform].push(account);
    });

    return Object.entries(grouped).map(([title, accts]) => ({
      title,
      accounts: accts,
    }));
  }, [accounts]);
}
